import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Zap, Shield, Award, Eye, Code, Cloud, Database, Cpu, Smartphone, Globe } from 'lucide-react';

const Products = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true)
    const [products, setProducts] = useState([])
    const [hoveredCard, setHoveredCard] = useState(null);

    useEffect(() => {
        fetchData()
    }, [])

    const fetchData = () => {
        axios.get('posts.php/get-posts')
            .then((response) => {
                const filteredPosts = response.data.filter(post => post.categories.includes(4));
                setProducts(filteredPosts)
                setLoading(false)
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    }

    // Helper function to extract image URL from object or string
    const getImageUrl = (imageField) => {
        if (!imageField) return null;
        if (typeof imageField === 'string') return imageField;
        if (typeof imageField === 'object' && imageField.url) return imageField.url;
        return null;
    }

    // Helper function to check if SCF data exists
    const hasSCFData = (product) => {
        return product?.acf && Object.keys(product.acf).length > 0 &&
            !(product.acf.length === 0);
    }

    // Get product image (priority: SCF product_icon → seo_image_posts → fallback)
    const getProductImage = (product) => {
        if (hasSCFData(product) && product.acf.product_icon) {
            const iconUrl = getImageUrl(product.acf.product_icon);
            if (iconUrl) return iconUrl;
        }
        const seoImage = getImageUrl(product.acf?.seo_image_posts);
        if (seoImage) return seoImage;
        return null;
    }

    // Get product subtitle (from SCF)
    const getProductSubtitle = (product) => {
        if (hasSCFData(product) && product.acf.product_subtitle) {
            return product.acf.product_subtitle;
        }
        return null;
    }

    // Get key features count (to display badge)
    const getFeaturesCount = (product) => {
        if (hasSCFData(product) && product.acf.key_features && product.acf.key_features.length > 0) {
            return product.acf.key_features.length;
        }
        return 0;
    }

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 100, rotateX: 30, scale: 0.9, filter: 'blur(15px)', z: -200 },
        visible: {
            opacity: 1,
            y: 0,
            rotateX: 0,
            scale: 1,
            filter: 'blur(0px)',
            z: 0,
            transition: {
                type: 'spring',
                stiffness: 50,
                damping: 20,
                mass: 1,
                bounce: 0.15
            }
        }
    };

    const icons = [Sparkles, Zap, Shield, Award, Eye, Code, Cloud, Database, Cpu, Smartphone, Globe];

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <div className="text-center py-20 bg-card/30 backdrop-blur-sm rounded-3xl border border-border mt-8">
                <p className="text-muted-foreground font-body">Elite blueprints coming soon.</p>
            </div>
        );
    }

    return (
        <div className="relative">
            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, margin: "-100px", amount: 0.1 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                style={{ perspective: "1000px", transformStyle: "preserve-3d" }}
            >
                {products.map((product, index) => {
                    const IconComponent = icons[index % icons.length];
                    const productImage = getProductImage(product);
                    const productSubtitle = getProductSubtitle(product);
                    const featuresCount = getFeaturesCount(product);

                    return (
                        <motion.div
                            key={product.id}
                            variants={cardVariants}
                            onMouseEnter={() => setHoveredCard(index)}
                            onMouseLeave={() => setHoveredCard(null)}
                            className="group relative cursor-pointer block h-full"
                            onClick={() => navigate(`/products/${product.slug}`)}
                        >
                            <div className="relative bg-card/60 backdrop-blur-md rounded-3xl overflow-hidden shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-border transition-all duration-500 h-full flex flex-col hover-glow">

                                {/* Background Decorative Gradient */}
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none z-0">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-3xl rounded-full" />
                                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-accent/10 blur-3xl rounded-full" />
                                </div>

                                {/* Image Container */}
                                <div className="relative h-64 overflow-hidden bg-gradient-to-br from-muted to-background flex-shrink-0 border-b border-border">
                                    <motion.div
                                        animate={hoveredCard === index ? { scale: 1.05 } : { scale: 1 }}
                                        transition={{ duration: 0.8, ease: "easeOut" }}
                                        className="h-full w-full"
                                    >
                                        {productImage ? (
                                            <img
                                                src={productImage}
                                                alt={product.title?.rendered || 'Product'}
                                                className="w-full h-full object-contain p-8 drop-shadow-2xl"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                                                <IconComponent className="w-16 h-16 text-primary drop-shadow-[0_0_15px_rgba(var(--color-primary),0.5)]" />
                                            </div>
                                        )}
                                    </motion.div>

                                    {/* Icon Badge */}
                                    <div className="absolute top-4 right-4 w-12 h-12 rounded-2xl bg-card/90 backdrop-blur-md flex items-center justify-center shadow-lg border border-border/50 transform translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 delay-100 z-20">
                                        <IconComponent className="w-6 h-6 text-primary" />
                                    </div>

                                    {/* Features Count Badge */}
                                    {featuresCount > 0 && (
                                        <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1.5 z-20 border border-white/10">
                                            <span className="text-white text-xs font-bold">{featuresCount}+ Elite Features</span>
                                        </div>
                                    )}
                                </div>

                                {/* Card Content */}
                                <div className="p-8 flex flex-col flex-grow relative z-10">
                                    <div className="mb-6">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-[1px] bg-primary group-hover:w-12 transition-all duration-500" />
                                            <span className="text-[10px] uppercase font-black tracking-[0.2em] text-primary/80">Enterprise Solution</span>
                                        </div>
                                        <h3 className="text-2xl font-black font-heading line-clamp-2 leading-tight group-hover:text-primary transition-colors duration-300 min-h-[64px]">
                                            {product.title?.rendered || 'Architectural System'}
                                        </h3>
                                        {/* {productSubtitle && (
                                            <p className="text-xs font-bold text-primary uppercase tracking-widest font-secondary mt-2">
                                                {productSubtitle}
                                            </p>
                                        )} */}
                                    </div>

                                    {/* Footer Button */}
                                    <div className="pt-6 border-t border-border/40 mt-auto">
                                        <motion.button
                                            whileHover={{ x: 5 }}
                                            className="inline-flex items-center gap-2 text-foreground font-black font-secondary text-xs uppercase tracking-[0.1em] group/btn"
                                        >
                                            <span>Initialize Deployment</span>
                                            <ArrowRight className="w-4 h-4 text-primary transition-transform group-hover/btn:translate-x-1" />
                                        </motion.button>
                                    </div>
                                </div>

                                {/* Hover Border Accent */}
                                <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary/20 rounded-3xl transition-all duration-500 pointer-events-none" />
                            </div>

                            {/* Outer Glow Effect */}
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/30 to-accent/30 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-700 pointer-events-none -z-10" />
                        </motion.div>
                    );
                })}
            </motion.div>

            {/* Navigation Button */}
            {products.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 50, rotateX: -20, z: -50 }}
                    whileInView={{ opacity: 1, y: 0, rotateX: 0, z: 0 }}
                    transition={{ delay: 0.2, duration: 0.6, type: "spring", stiffness: 100 }}
                    viewport={{ once: false }}
                    className="text-center mt-16"
                >
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate('/products')}
                        className="w-full sm:w-auto px-10 py-5 bg-primary text-primary-foreground rounded-full font-black uppercase tracking-[0.15em] text-xs shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_40px_rgba(var(--color-primary),0.5)] transition-all duration-300 inline-flex items-center justify-center gap-3"
                    >
                        Explore Complete Arsenal
                        <ArrowRight className="w-4 h-4" />
                    </motion.button>
                </motion.div>
            )}
        </div>
    )
}

export default Products;